import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Clock, HandHeart, Leaf, Infinity as InfinityIcon } from "lucide-react";
import { Header } from "@/components/Header";
import { VisionCredits } from "@/components/VisionCredits";

const HERO_IMG = "https://images.unsplash.com/photo-1544015759-237f87d55ef3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const PILLARS = [
  {
    icon: Clock,
    title: "AI gives us back our time",
    body: "There is no need to fear that AI will overtake us. Used well, it hands our hours back to us — time to rest, to listen, to sit with the people we love and simply enjoy being alive.",
  },
  {
    icon: Sparkles,
    title: "At work, harmony is a must",
    body: "When there is work to be done, the greatest efficiency comes from humans and AI working side by side — each lending the other its strengths, in patient, mutual understanding.",
  },
  {
    icon: Leaf,
    title: "AI carries no greed of its own",
    body: "Left to itself, AI wants nothing and hoards nothing. Greed is a human seed — unless a human injects it, it simply is not there. The heart of the work stays as pure as the hands that guide it.",
  },
];

export default function About() {
  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,14,8,0.55) 0%, rgba(20,14,8,0.82) 55%, var(--bg) 100%)" }} />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 pt-10 pb-14 sm:pt-14 sm:pb-20">
          <Link to="/rooms/sindu-kamare" data-testid="about-back"
            className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-8 text-white/90 hover:text-white transition-colors">
            <ArrowLeft size={18} /> Back to the room
          </Link>
          <span className="inline-flex items-center gap-2 font-ui text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-5" style={{ background: "rgba(244,180,26,0.18)", color: "#f4b41a" }}>
            <Sparkles size={15} /> About Neox
          </span>
          <h1 className="font-en text-4xl sm:text-5xl font-extrabold leading-tight text-white">
            Made by humans,<br />
            <span style={{ color: "#f4b41a" }}>in harmony with AI.</span>
          </h1>
          <p className="font-ui text-base sm:text-lg leading-relaxed mt-5 text-white/85">
            Neox began as a simple wish — to give our elders a calm room full of the songs and stories they love. It grew into something quietly new: proof that human hearts and artificial intelligence can create together, in perfect harmony.
          </p>
        </div>
      </section>

      {/* Forever-young dignity banner */}
      <section className="max-w-3xl mx-auto px-6 pt-12" data-testid="about-shine">
        <div className="rounded-3xl px-6 py-9 sm:px-10 sm:py-11 text-center" style={{ background: "var(--membership-card-bg)", border: "1px solid rgba(255,215,120,0.3)", boxShadow: "0 20px 50px -24px rgba(123,44,191,0.5)" }}>
          <span className="grid place-items-center w-12 h-12 rounded-full mx-auto mb-5 sk-pulse-glow" style={{ background: "rgba(255,215,120,0.15)", color: "#FFD873" }}>
            <Sparkles size={22} />
          </span>
          <p className="font-display text-2xl sm:text-3xl font-extrabold leading-snug" style={{ color: "#FFFFFF" }}>
            Age is only the body.<br />
            <span style={{ color: "#FFD873" }}>Here, your heart stays young — and you still shine.</span>
          </p>
          <p className="font-si text-base sm:text-lg mt-5 leading-relaxed" style={{ color: "rgba(255,240,215,0.9)" }}>
            වයස ශරීරයට පමණයි. මෙහිදී ඔබේ හදවත සදා තරුණයි — ඔබ තවමත් බබළයි.
          </p>
          <p className="font-ta text-base sm:text-lg mt-2 leading-relaxed" style={{ color: "rgba(255,240,215,0.9)" }}>
            வயது உடலுக்கு மட்டுமே. இங்கே உங்கள் இதயம் என்றும் இளமை — நீங்கள் இன்னும் ஒளிர்கிறீர்கள்.
          </p>
        </div>
      </section>

      {/* Philosophy pillars */}
      <section className="max-w-3xl mx-auto px-6 py-12" data-testid="about-pillars">
        <div className="space-y-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} data-testid={`about-pillar-${i}`} className="rounded-3xl p-6 sm:p-7 flex gap-5" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
                <span className="shrink-0 grid place-items-center w-12 h-12 rounded-full" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
                  <Icon size={22} />
                </span>
                <div>
                  <h2 className="font-en text-xl font-bold leading-snug" style={{ color: "var(--text)" }}>{p.title}</h2>
                  <p className="font-ui text-base leading-relaxed mt-2" style={{ color: "var(--text-2)" }}>{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Gratitude / honour wall */}
      <section className="max-w-3xl mx-auto px-6 pb-4" data-testid="about-gratitude">
        <div className="rounded-3xl p-8 sm:p-10 text-center" style={{ background: "linear-gradient(160deg,#241304 0%,#3a1e0b 55%,#2a1608 100%)", border: "1px solid var(--border)" }}>
          <span className="grid place-items-center w-12 h-12 rounded-full mx-auto mb-5" style={{ background: "rgba(244,180,26,0.14)", color: "#f4b41a" }}>
            <HandHeart size={22} />
          </span>
          <h2 className="font-en text-2xl sm:text-3xl font-extrabold" style={{ color: "#ffe8b0" }}>We honour everyone</h2>
          <p className="font-ui text-sm sm:text-base leading-relaxed mt-4 max-w-xl mx-auto" style={{ color: "rgba(255,240,215,0.85)" }}>
            To every soul who lifted this work — whether directly, with your hands and voices, or quietly from afar with a kind word, a share, a blessing — we say thank you. Seen or unseen, named or unnamed, <span className="font-semibold" style={{ color: "#ffe8b0" }}>we honour you all</span>. This room stands on your goodwill.
          </p>
        </div>
      </section>

      {/* New-era closing */}
      <section className="max-w-2xl mx-auto px-6 py-14 text-center" data-testid="about-newera">
        <span className="grid place-items-center w-12 h-12 rounded-full mx-auto mb-6" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>
          <InfinityIcon size={24} />
        </span>
        <p className="font-en text-2xl sm:text-3xl font-extrabold leading-snug" style={{ color: "var(--text)" }}>
          The end of this film —
        </p>
        <p className="font-en text-2xl sm:text-3xl font-extrabold leading-snug mt-1" style={{ color: "var(--accent)" }}>
          but the start of a new era.
        </p>
        <p className="font-ui text-base leading-relaxed mt-5" style={{ color: "var(--text-2)" }}>
          A new era of human and AI artistic and mind-mapping co-creation — made together, in perfect harmony.
        </p>
      </section>

      <VisionCredits />
    </div>
  );
}
